import * as React from 'react';

import {
  TableColumns,
  TextLink,
  Status,
  Icon,
} from '@teamsnap/snap-ui';
import { DateFormat, DateService } from 'frontend-toolkit';
import { formatPenniesToDollars } from 'core/utils';
import { MemberInvoice, InvoiceStatus } from './types';
import { TEAMSNAP_URL } from 'core/constants';

const invoiceStatus = (row: MemberInvoice) => (
  <>
    {row?.status === InvoiceStatus.SCHEDULED && (
      <Status state="success">
        <Icon name="check" size="s" />
        Open (Scheduled)
      </Status>
    )}

    {row?.status === InvoiceStatus.FAILED && (
      <Status state="negative">
        <Icon name="schedule" size="s" />
        Open (Failed)
      </Status>
    )}

    {row?.status === InvoiceStatus.OPEN && (
      <Status state="negative">
        <Icon name="schedule" size="s" />
        Open
      </Status>
    )}

    {row?.status === InvoiceStatus.CANCELED && (
      <Status state="inactive">Canceled</Status>
    )}

    {row?.status === InvoiceStatus.PAID && (
      <Status state="success">
        <Icon name="check" size="s" />
        Paid
      </Status>
    )}

    {row?.status === InvoiceStatus.SUBMITTED && (
      <Status state="warning">
        <Icon name="schedule" size="s" />
        Submitted
      </Status>
    )}
  </>
);

export const COLUMNS: TableColumns = {
  dueDate: {
    label: 'Due Date',
    sort: 'date',
    align: 'left',
    width: '120px',
    getBodyProps: (row: MemberInvoice) => ({
      height: 'tall',
      type: 'custom',
      children: DateService.format(
        new Date(row.dueDate),
        DateFormat.DD_MM_YYYY
      ),
    }),
  },
  name: {
    label: 'Invoice',
    sort: 'alpha',
    align: 'left',
    width: '400px',
    headerProps: {
      tooltip: 'Team-issued invoices are not included.',
    },
    getBodyProps: (row: MemberInvoice) => ({
      height: 'tall',
      type: 'custom',
      children: (
        <div className="sui-w-[calc(400px-32px)] sui-overflow-hidden sui-whitespace-nowrap sui-flex sui-flex-col sui-gap-y-[4px]">
          <span title={row.name} className="sui-body-dense sui-truncate">
            {row.name}
          </span>
          <span
            title={`${row.programName} · ${row.seasonName}`}
            className="sui-caption sui-text-neutral-text-medium sui-truncate"
          >
            {row.programName} · {row.seasonName}
          </span>
        </div>
      ),
    }),
  },
  status: {
    label: 'Status',
    sort: 'alpha',
    align: 'left',
    width: '250px',
    getBodyProps: (row: MemberInvoice) => ({
      height: 'tall',
      type: 'custom',
      children: invoiceStatus(row),
    }),
  },
  total: {
    label: 'Total',
    sort: 'numeric',
    align: 'right',
    width: '120px',
    getBodyProps: (row) => ({
      height: 'tall',
      type: 'custom',
      children: formatPenniesToDollars(row.total),
    }),
  },
  balance: {
    label: 'Balance',
    sort: 'numeric',
    align: 'right',
    width: '120px',
    getBodyProps: (row) => ({
      height: 'tall',
      type: 'custom',
      children: formatPenniesToDollars(row.balance),
    }),
  },
  actions: {
    label: 'Actions',
    align: 'left',
    width: '400px',
    getBodyProps: (row: MemberInvoice) => ({
      height: 'tall',
      type: 'custom',
      children: (
        <div className="sui-flex sui-gap-3">
          <TextLink
            data-testid="MemberInvoices__view-batch"
            variantType="primary"
            href={`${TEAMSNAP_URL}/${row.divisionId}/league_payments/invoicing/${row.batchInvoiceId}/invoices`}
            className="sui-whitespace-nowrap"
          >
            View Batch
          </TextLink>

          <TextLink
            data-testid="MemberInvoices__view-invoice"
            variantType="primary"
            href={`${TEAMSNAP_URL}/${row.divisionId}/league_payments/invoicing/${row.batchInvoiceId}/invoices/${row.invoiceId}`}
            className="sui-whitespace-nowrap"
          >
            View Invoice
          </TextLink>
        </div>
      ),
    }),
  },
};
